import React from 'react';
import { Box, Typography, Container, Paper, Button, Grid } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import SchoolIcon from '@mui/icons-material/School';
import LocalLibraryIcon from '@mui/icons-material/LocalLibrary';
import SportsScoreIcon from '@mui/icons-material/SportsScore';
import { useAuth } from '../../contexts/AuthContext';
import MainLayout from '../../components/layout/MainLayout';

const ComingSoonPage: React.FC = () => {
  const theme = useTheme();
  const { logout } = useAuth();

  return (
    <MainLayout>
      <Container maxWidth="md" sx={{ py: 5 }}>
        <Paper
          elevation={3}
          sx={{
            p: 5,
            textAlign: 'center',
            borderRadius: 4,
            background: `linear-gradient(135deg, ${theme.palette.primary.light} 0%, ${theme.palette.primary.main} 100%)`,
            color: 'white',
            position: 'relative',
            overflow: 'hidden',
          }}
        >
          {/* Animated background elements */}
          <Box sx={{
            position: 'absolute',
            top: -20,
            right: -20,
            width: 100,
            height: 100,
            borderRadius: '50%',
            backgroundColor: 'rgba(255,255,255,0.2)',
            animation: 'float 8s infinite ease-in-out',
            '@keyframes float': {
              '0%, 100%': { transform: 'translate(0, 0) rotate(0deg)' },
              '50%': { transform: 'translate(20px, 20px) rotate(10deg)' },
            }
          }} />
          
          <Box sx={{
            position: 'absolute',
            bottom: -30,
            left: -30,
            width: 150,
            height: 150,
            borderRadius: '50%',
            backgroundColor: 'rgba(255,255,255,0.15)',
            animation: 'float2 10s infinite ease-in-out',
            '@keyframes float2': {
              '0%, 100%': { transform: 'translate(0, 0) rotate(0deg)' },
              '50%': { transform: 'translate(-20px, 20px) rotate(-10deg)' },
            }
          }} />

          <Typography variant="h2" component="h1" sx={{ 
            fontWeight: 'bold', 
            mb: 2, 
            fontSize: { xs: '2.5rem', md: '3.5rem' },
            textShadow: '2px 2px 4px rgba(0,0,0,0.2)'
          }}>
            MathWiz League
          </Typography>
          
          <Typography variant="h4" component="h2" sx={{ 
            mb: 4, 
            fontWeight: 500,
            fontSize: { xs: '1.5rem', md: '2rem' },
            color: theme.palette.secondary.light
          }}>
            Coming Soon!
          </Typography>
          
          <Typography variant="body1" sx={{ 
            mb: 4, 
            fontSize: { xs: '1rem', md: '1.2rem' },
            maxWidth: '700px',
            mx: 'auto'
          }}>
            We're putting the finishing touches on an exciting new version of MathWiz League! 
            Your account is ready, and we'll let you know when everything is set up. 
            Get ready for awesome math challenges and achievements!
          </Typography>

          <Grid container spacing={3} sx={{ mb: 5, justifyContent: 'center' }}>
            <Grid item xs={12} sm={6} md={3}>
              <Paper elevation={6} sx={{ 
                p: 2, 
                height: '100%',
                display: 'flex', 
                flexDirection: 'column', 
                alignItems: 'center',
                backgroundColor: 'rgba(255,255,255,0.9)',
                transition: 'transform 0.3s',
                '&:hover': {
                  transform: 'translateY(-5px)',
                }
              }}>
                <SchoolIcon sx={{ fontSize: 50, color: theme.palette.primary.main, mb: 1 }} />
                <Typography variant="h6" color="text.primary">
                  Math Mastery
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Challenge yourself with exciting math problems
                </Typography>
              </Paper>
            </Grid>
            
            <Grid item xs={12} sm={6} md={3}>
              <Paper elevation={6} sx={{ 
                p: 2, 
                height: '100%',
                display: 'flex', 
                flexDirection: 'column', 
                alignItems: 'center',
                backgroundColor: 'rgba(255,255,255,0.9)',
                transition: 'transform 0.3s',
                '&:hover': {
                  transform: 'translateY(-5px)',
                }
              }}>
                <EmojiEventsIcon sx={{ fontSize: 50, color: theme.palette.primary.main, mb: 1 }} />
                <Typography variant="h6" color="text.primary">
                  Achievements
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Earn cool badges as you solve problems
                </Typography>
              </Paper>
            </Grid>
            
            <Grid item xs={12} sm={6} md={3}>
              <Paper elevation={6} sx={{ 
                p: 2, 
                height: '100%',
                display: 'flex', 
                flexDirection: 'column', 
                alignItems: 'center',
                backgroundColor: 'rgba(255,255,255,0.9)',
                transition: 'transform 0.3s',
                '&:hover': {
                  transform: 'translateY(-5px)',
                }
              }}>
                <LocalLibraryIcon sx={{ fontSize: 50, color: theme.palette.primary.main, mb: 1 }} />
                <Typography variant="h6" color="text.primary">
                  Learning
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Improve your math skills through practice
                </Typography>
              </Paper>
            </Grid>
            
            <Grid item xs={12} sm={6} md={3}>
              <Paper elevation={6} sx={{ 
                p: 2, 
                height: '100%',
                display: 'flex', 
                flexDirection: 'column', 
                alignItems: 'center',
                backgroundColor: 'rgba(255,255,255,0.9)',
                transition: 'transform 0.3s',
                '&:hover': {
                  transform: 'translateY(-5px)',
                }
              }}>
                <SportsScoreIcon sx={{ fontSize: 50, color: theme.palette.primary.main, mb: 1 }} />
                <Typography variant="h6" color="text.primary">
                  Competition
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  See how you rank on the leaderboard
                </Typography>
              </Paper>
            </Grid>
          </Grid>

          <Button 
            onClick={() => logout()}
            variant="outlined" 
            size="large"
            sx={{ 
              color: 'white',
              borderColor: 'white',
              '&:hover': {
                borderColor: theme.palette.secondary.light,
                backgroundColor: 'rgba(255,255,255,0.1)',
              }
            }}
          >
            Log Out
          </Button>
        </Paper>
      </Container>
    </MainLayout>
  );
};

export default ComingSoonPage;